'use client'

import { useEffect, useRef, useState } from 'react'

import { cn } from '@/core/utils/common'
import { fmt } from '@/features/veluvit/lib/format'
import { usePaymentFlowStore } from '@/features/veluvit/stores/payment-flow.store'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

/** 잔액은 서버 값으로 0.4초 동안 이동하며, 확정된 충전의 획득량만 따로 떠오른다. */
export default function GemCount({ className }: { className?: string }) {
  const ownerId = useSessionStore((state) => state.data?.user?.id)
  return (
    <BalanceAnimation key={ownerId} ownerId={ownerId} className={className} />
  )
}

function BalanceAnimation({
  ownerId,
  className,
}: {
  ownerId?: string
  className?: string
}) {
  const gem = useSessionStore((state) => state.data?.wallet.balance ?? 0)
  const gain = usePaymentFlowStore((state) => state.gain)
  const [shown, setShown] = useState(gem)
  const [flash, setFlash] = useState(false)
  const [expiredGain, setExpiredGain] = useState<typeof gain>(null)
  const activeGain =
    gain?.ownerId === ownerId && gain !== expiredGain ? gain : null
  const gainAmount = activeGain?.amount ?? 0
  const displayed = useRef(gem)

  useEffect(() => {
    const start = displayed.current
    if (start === gem) return
    setFlash(gem < start)
    const t0 = performance.now()
    let frame = 0
    let timer: ReturnType<typeof setTimeout> | undefined
    const step = (time: number) => {
      const progress = Math.min(1, (time - t0) / 400)
      const next = Math.round(start + (gem - start) * progress)
      displayed.current = next
      setShown(next)
      if (progress < 1) frame = requestAnimationFrame(step)
      else timer = setTimeout(() => setFlash(false), 250)
    }
    frame = requestAnimationFrame(step)
    return () => {
      cancelAnimationFrame(frame)
      if (timer) clearTimeout(timer)
    }
  }, [gem])

  useEffect(() => {
    if (!activeGain) return
    const timer = setTimeout(() => setExpiredGain(activeGain), 1700)
    return () => clearTimeout(timer)
  }, [activeGain])

  return (
    <span
      className={cn(
        'relative inline-flex transition-colors duration-200',
        flash && 'animate-shake text-coral!',
        gainAmount > 0 && 'text-sub!',
        className
      )}
    >
      {fmt(shown)}
      {gainAmount > 0 && (
        <span
          key={gain?.orderId}
          aria-hidden="true"
          className="pointer-events-none absolute -top-5 right-0 whitespace-nowrap text-c12 font-bold leading-4 text-sub animate-[gem-gain_1.6s_ease-out_both] over10:text-c13"
        >
          +{fmt(gainAmount)}
        </span>
      )}
    </span>
  )
}
